// istanbul ignore file — composition shell; logic mirrors `LoginPage` + activity log from `SecurityPage`.

import { useFlag, useVariant } from '@calendly/feature-check-react'
import { I18n } from '@calendly/i18n'
import { TitleText } from '@calendly/ui/components/typography'
import { Redirect, Route, Switch, useLocation } from 'react-router-dom'

import { useGetPolicyQuery } from 'common'
import LoadingState from 'components/core/loading_state'
import { PageHeaderWithTabs, TabContainer, TabContent } from 'components/page_elements'
import { ActivityLog } from 'features/activity_log'
import { generateTabs, getAdminAccessModalTabs } from 'features/admin'
import { CRM_ORG_SETTINGS_FLAG_KEY } from 'features/crm'
import {
  DomainControl,
  SingleSignOn,
  UserAccess,
  UserProvisioning,
} from 'features/organization_settings'
import { OBI_HOME_NEW_JOIN_AN_ORG_VARIANT } from 'features/settings_modal/constants'
import { useIsSettingsModalLayout } from 'features/settings_modal/layout_context'

import { SettingsModalIntegrationsPage } from './settings_modal_integrations_page'

const ActivityLogTab = () => (
  <TabContainer>
    <TitleText level={2} size="medium">
      {I18n.t('publisher.admin.security.tabs.activity_log')}
    </TitleText>
    <TabContent>
      <ActivityLog />
    </TabContent>
  </TabContainer>
)

const firstAllowedAccessPath = ({
  base,
  joinAnOrgEnabled,
  canManageDomains,
  canManageUserAccess,
  canManageSso,
  canManageUserProvisioning,
}) => {
  if (joinAnOrgEnabled) {
    if (canManageUserAccess) return `${base}/user_access`
    if (canManageSso) return `${base}/sso`
    if (canManageUserProvisioning) return `${base}/user_provisioning`
    return `${base}/activity_log`
  }
  if (canManageDomains) return `${base}/domain_control`
  if (canManageSso) return `${base}/sso`
  if (canManageUserProvisioning) return `${base}/user_provisioning`
  return `${base}/activity_log`
}

export const SettingsModalAccessPage = ({ match }) => {
  const base = match.path
  const { pathname } = useLocation()
  const inSettingsModal = useIsSettingsModalLayout()
  /** Settings modal shell renders {@link AdminAccessSettingsModalTabbedHeader}; omit duplicate tab row. */
  const settingsModalShellOwnsTabs = inSettingsModal
  const joinAnOrgEnabled = useVariant(OBI_HOME_NEW_JOIN_AN_ORG_VARIANT) === 'on'
  const integrationsSettingsEnabled = useFlag(CRM_ORG_SETTINGS_FLAG_KEY)

  const { data: policyData = {}, isFetching: isPolicyFetching } = useGetPolicyQuery()
  const {
    canManageSso,
    canManageUserProvisioning,
    canManageUserAccess,
    canManageDomains,
    canManageOrganizationIntegrationSettings,
  } = policyData || {}
  const canManageIntegrationsTab = integrationsSettingsEnabled && canManageOrganizationIntegrationSettings

  if (isPolicyFetching) {
    return <LoadingState />
  }

  const tabs = getAdminAccessModalTabs({
    base,
    joinAnOrgEnabled,
    canManageSso,
    canManageUserProvisioning,
    canManageUserAccess,
    canManageDomains,
    canManageOrganizationIntegrationSettings,
    integrationsSettingsEnabled,
  })

  const defaultPath = firstAllowedAccessPath({
    base,
    joinAnOrgEnabled,
    canManageDomains,
    canManageUserAccess,
    canManageSso,
    canManageUserProvisioning,
  })

  const routes = (
    <Switch>
      <Redirect from={base} to={defaultPath} exact />
      {canManageSso && (
        <Route
          path={`${base}/sso`}
          exact
          render={() => (
            <TabContainer narrow>
              <SingleSignOn />
            </TabContainer>
          )}
        />
      )}
      {canManageUserProvisioning && (
        <Route
          path={`${base}/user_provisioning`}
          exact
          render={() => (
            <TabContainer narrow>
              <UserProvisioning />
            </TabContainer>
          )}
        />
      )}
      {joinAnOrgEnabled
        ? canManageUserAccess && (
          <Route
            path={`${base}/user_access`}
            exact
            render={() => (
              <TabContainer narrow>
                <UserAccess />
              </TabContainer>
            )}
          />
        )
        : canManageDomains && (
          <Route
            path={`${base}/domain_control`}
            exact
            render={() => (
              <TabContainer narrow>
                <DomainControl />
              </TabContainer>
            )}
          />
        )}
      <Route path={`${base}/activity_log`} exact component={ActivityLogTab} />
      {canManageIntegrationsTab && (
        <Route
          path={`${base}/integrations`}
          exact
          render={() => (
            <TabContainer narrow>
              <SettingsModalIntegrationsPage />
            </TabContainer>
          )}
        />
      )}
      <Redirect to={defaultPath} />
    </Switch>
  )

  if (settingsModalShellOwnsTabs) {
    return routes
  }

  return (
    <>
      <PageHeaderWithTabs
        title={I18n.t('publisher.admin.navigation.access')}
        tabs={generateTabs({ pathname, tabs })}
      />
      {routes}
    </>
  )
}
